// Confirmation as a function call, not as component state.
//
// A destructive action asks, and awaits the answer, rather than carrying its
// own dialog state — which is a copy per call site, each free to forget the
// busy state or the wording:
//
//     const confirm = useConfirm();
//     if (await confirm({ title: "Delete task", message: … })) del.mutate(id);
//
// or, when the dialog should stay open and disabled until the write lands:
//
//     await confirm({ title: …, message: …, action: () => del.mutateAsync(id) });
//
// `ConfirmDialog` (the shape) stays in components/ui. This is the behaviour.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ConfirmDialog } from "@/platform/ui/primitives/index.js";

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  /** Defaults to "Delete" — the overwhelmingly common case. */
  confirmLabel?: string;
  /** Defaults to "danger" (red confirm, Cancel focused). Pass "positive" for a
   *  reversible/affirmative action — green confirm button, focused first so Enter
   *  confirms. */
  tone?: "danger" | "positive";
  /**
   * Run while the dialog stays open and its button is disabled. The dialog
   * closes when this resolves; it stays open if it throws, so the caller can
   * surface the error without the confirmation vanishing underneath it.
   */
  action?: () => Promise<unknown>;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  // Kept in a ref so `confirm` never changes identity and can be depended on.
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  const settle = useCallback((ok: boolean) => {
    pendingRef.current?.resolve(ok);
    setPending(null);
    setBusy(false);
  }, []);

  const onConfirm = useCallback(async () => {
    const current = pendingRef.current;
    if (!current) return;
    if (!current.action) return settle(true);
    setBusy(true);
    try {
      await current.action();
      settle(true);
    } catch {
      // Leave the dialog open: the caller reports the failure.
      setBusy(false);
    }
  }, [settle]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pending != null}
        title={pending?.title ?? ""}
        message={pending?.message ?? null}
        confirmLabel={pending?.confirmLabel ?? "Delete"}
        tone={pending?.tone ?? "danger"}
        busy={busy}
        onConfirm={onConfirm}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

/** Ask the user to confirm. Resolves true if they did. */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  return confirm;
}
